import { useId } from "react";
import { IconLink } from "@tabler/icons-react";

import { Button } from "@/components/ui/Button/Button.tsx";
import type {
  FileSharePermission,
  ShareLinkAudience,
  ShareLinkOptions,
} from "@/api/files-hub/fileShares.api";
import {
  audienceTexts,
  PERMISSION_OPTIONS,
  permissionLabel,
} from "@/components/features/FileManager/shareLabels.utils";

import { RolePicker } from "./RolePicker";
import styles from "./GeneralAccess.module.scss";

const DEFAULT_OPTIONS: ShareLinkOptions = {
  audiences: ["RESTRICTED", "ENTITY"],
  editAudiences: [],
};

export interface GeneralAccessProps {
  audience: ShareLinkAudience;
  entityName: string;
  options?: ShareLinkOptions;
  roleIds?: string[];
  permission?: FileSharePermission;
  error?: string | null;
  onAudienceChange: (audience: ShareLinkAudience) => void;
  onRolesChange?: (roleIds: string[]) => void;
  onSaveRoles?: () => void;
  canSaveRoles?: boolean;
  onPermissionChange?: (permission: FileSharePermission) => void;
  onCopy: () => void;
  onReset?: () => void;
  disabled?: boolean;
}

export function GeneralAccess({
  audience,
  entityName,
  options = DEFAULT_OPTIONS,
  roleIds = [],
  permission = "VIEW",
  error,
  onAudienceChange,
  onRolesChange,
  onSaveRoles,
  canSaveRoles = false,
  onPermissionChange,
  onCopy,
  onReset,
  disabled = false,
}: GeneralAccessProps) {
  const name = useId();
  const permissionId = useId();
  const texts = audienceTexts(entityName);
  const canEdit = options.editAudiences.includes(audience);

  return (
    <fieldset className={styles.root} disabled={disabled}>
      <legend className={styles.title}>General access</legend>
      <div className={styles.options}>
        {options.audiences.map((value) => (
          <label key={value} className={styles.option}>
            <input
              type="radio"
              name={name}
              className={styles.radio}
              value={value}
              checked={audience === value}
              onChange={() => onAudienceChange(value)}
            />
            <span className={styles.text}>
              <span className={styles.label}>{texts[value].label}</span>
              <span className={styles.description}>{texts[value].description}</span>
            </span>
          </label>
        ))}
      </div>
      {audience === "ENTITY_ROLES" && onRolesChange && (
        <div className={styles.roles}>
          <RolePicker selectedIds={roleIds} onChange={onRolesChange} />
          {onSaveRoles && (
            <Button
              type="button"
              variant="primary"
              disabled={!canSaveRoles || roleIds.length === 0}
              onClick={onSaveRoles}
            >
              Save roles
            </Button>
          )}
        </div>
      )}
      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}
      <div className={styles.footer}>
        {canEdit && onPermissionChange ? (
          <>
            <label htmlFor={permissionId} className={styles.srOnly}>
              Link permission
            </label>
            <select
              id={permissionId}
              className={styles.select}
              value={permission}
              onChange={(e) => onPermissionChange(e.target.value as FileSharePermission)}
            >
              {PERMISSION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </>
        ) : (
          <span className={styles.permission}>{permissionLabel("VIEW")}</span>
        )}
        <div className={styles.actions}>
          {onReset && (
            <Button type="button" variant="neutral-secondary" onClick={onReset}>
              Reset link
            </Button>
          )}
          <Button
            type="button"
            variant="neutral-secondary"
            iconLeft={<IconLink size={16} />}
            onClick={onCopy}
          >
            Copy link
          </Button>
        </div>
      </div>
    </fieldset>
  );
}
